import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import MainPage from './pages/MainPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import SettingsPage from './pages/SettingsPage';
import { useAuthState } from 'react-firebase-hooks/auth';
import { portalAuth } from './utils/firebase';
import { APP_VERSION } from './utils/version';
import { toast, Toaster } from 'react-hot-toast';

// Protected route wrapper
const ProtectedRoute = ({ children }) => {
  const [user, loading] = useAuthState(portalAuth);
  
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }
  
  if (!user) {
    return <Navigate to="/login" />;
  }
  
  return children;
};

function App() {
  const [showUpdateButton, setShowUpdateButton] = useState(false);
  const [currentVersion, setCurrentVersion] = useState(APP_VERSION);

  // Check for app updates
  const checkVersion = async () => {
    try {
      const response = await fetch('/version.json?' + new Date().getTime());
      const data = await response.json();
      
      setCurrentVersion(data.version);
      if (data.version !== APP_VERSION) {
        setShowUpdateButton(true);
      } else {
        setShowUpdateButton(false);
      }
    } catch (error) {
      console.error('Error checking version:', error);
    }
  };

  // Force reload to update the app
  const handleUpdate = () => {
    // Force reload from server, not from cache
    window.location.reload(true);
  };

  // Register service worker and set up version checking
  useEffect(() => {
    // Check version on mount and window focus
    checkVersion();
    window.addEventListener('focus', checkVersion);

    // Cleanup
    return () => {
      window.removeEventListener('focus', checkVersion);
    };
  }, []);

  // Redirect to dashboard if on MainPage and authenticated
  const MainPageWithAuth = () => {
    const [user] = useAuthState(portalAuth);
    
    if (user) {
      return <DashboardPage />;
    }
    
    return <MainPage />;
  };
  
  return (
    <>
      <Toaster 
        position="top-center"
        toastOptions={{
          style: {
            zIndex: 100 // Higher than notifications and action buttons
          }
        }}
      />
      
      {showUpdateButton && currentVersion !== APP_VERSION && (
        <div className="fixed top-16 left-0 right-0 flex justify-center z-[9999] px-4 py-2">
          <button
            onClick={handleUpdate}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg shadow-lg hover:bg-blue-600 transition-colors"
          >
            <span>🔄</span>
            New version available! Click to update
          </button>
        </div>
      )}
      
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<MainPageWithAuth />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          } />
          <Route path="/settings" element={
            <ProtectedRoute>
              <SettingsPage />
            </ProtectedRoute>
          } />
        </Routes>
      </BrowserRouter>
    </>
  );
}

export default App;